import { Camera, Vector2, Vector3, WebGLRenderer } from 'three';

import { LevelIndex } from './level-index';
import { TileCoord, TileDesire, Viewport } from './ortho-tile-typings';
import { TileQuadtree } from './tile-quad-tree';

type LodContext = {
  camera: Camera;
  viewport: Viewport;
  estimateScreenPx: (coord: TileCoord) => number;
  intersects?: (coord: TileCoord) => boolean;
  getTileCenter?: (coord: TileCoord) => Vector3;
};

/**
 * Selector config:
 * - splitThresholdPx decides when to subdivide.
 * - maxTiles is a safety cap.
 *
 * Important: to ensure “always render lowest zoom”, roots are ALWAYS selected (at least as desires),
 * and fallbackChain guarantees you can render a root even if nothing else is ready.
 */
export class TileLodSelector {
  private readonly splitThresholdPx: number;
  private readonly maxTiles: number;

  constructor(params: { splitThresholdPx: number; maxTiles: number }) {
    this.splitThresholdPx = params.splitThresholdPx;
    this.maxTiles = params.maxTiles;
  }

  getViewport(renderer: WebGLRenderer): Viewport {
    // renderer.getSize returns logical pixels
    const size = new Vector2();
    renderer.getSize(size);
    return { width: size.x, height: size.y, pixelRatio: renderer.getPixelRatio() };
  }

  selectTiles(params: {
    quadtree: TileQuadtree;
    levelIndex: LevelIndex;
    ctx: LodContext;
    out: TileDesire[];
  }): void {
    const { quadtree, levelIndex, ctx, out } = params;

    const intersects = ctx.intersects ?? (() => true);

    // roots
    const stack: TileCoord[] = [];
    for (const r of quadtree.roots) {
      if (!levelIndex.hasTile(r)) {
        continue;
      }
      stack.push(r);
    }

    while (stack.length && out.length < this.maxTiles) {
      const coord = stack.pop()!;
      if (!levelIndex.hasTile(coord)) {
        continue;
      }
      if (!intersects(coord)) {
        continue;
      }

      const px = ctx.estimateScreenPx(coord);

      // Always create parent desire for coverage guarantee
      const parentDesire: TileDesire = {
        coord,
        fallbackChain: levelIndex.getFallbackChain(coord, quadtree),
      };

      // Check if we can split: must be below maxZoom AND next zoom level must exist in levelIndex
      const nextZoom = coord.z + 1;
      const nextZoomExists = levelIndex.hasZoomLevel(nextZoom);
      const wantsSplit = coord.z < quadtree.maxZoom && nextZoomExists && px > this.splitThresholdPx;

      if (!wantsSplit) {
        out.push(parentDesire);
        continue;
      }

      // Attempt to refine: find children that exist (partial refinement allowed)
      const allChildren = quadtree.childrenOf(coord);
      const children = allChildren.filter((c) => levelIndex.hasTile(c));

      // If no children exist at that zoom/area, keep this tile only
      if (children.length === 0) {
        out.push(parentDesire);
        continue;
      }

      // Prioritize children by distance to camera (nearer tiles first)
      if (ctx.getTileCenter && children.length > 1) {
        children.sort((a, b) => {
          const centerA = ctx.getTileCenter!(a);
          const centerB = ctx.getTileCenter!(b);
          const distA = centerA.distanceTo(ctx.camera.position);
          const distB = centerB.distanceTo(ctx.camera.position);
          return distA - distB;
        });
      }

      // If we want split and have children, enqueue available children for more detail...
      // Push in reverse order so nearest tiles are popped first (LIFO stack)
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push(children[i]);
      }

      // Do NOT request the parent tile here. The root mosaic already guarantees coverage.
      // Only leaf desires are pushed; this avoids loading every ancestor LOD (z=minZoom..z-1)
      // before loading the desired tile (e.g. z=18).
    }
  }
}
